import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomModal from "../../common/CustomModal";
import CustomInput from "../../common/CustomInput";
import ActionSelect from "../../common/ActionSelect";
import UserAPIs from "../../../apiRoutes/User";
import AgencyAPIs from "../../../apiRoutes/Agency";
import RoleAPIs from "../../../apiRoutes/Role";
import { GET_AGENCIES_QUERY_KEY, GET_ROLES_QUERY_KEY, GET_PERMISSIONS_QUERY_KEY, GET_USERS_QUERY_KEY } from "../../../utils/constants/queryKeys";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import usePermissions from "../../../hooks/usePermissions";
import { PERMISSION_CODES } from "../../../utils/constants/permissions";
import toast from "react-hot-toast";
import useZustandStore from "../../../zustandStore/useZustandStore";
import { agencyDisplayName, emailHasSeekhoAppDomain, getAgencyIdFromUser, isAgencyScopedUser } from "../../../utils/agencyScope";
import { ChevronDown } from "lucide-react";
import { cn } from "../../../lib/utils";

const AddUserModal = ({ open, onClose, editData = null }) => {
    const [formData, setFormData] = useState({
        username: editData?.username || "",
        email: editData?.email || "",
        first_name: editData?.first_name || "",
        last_name: editData?.last_name || "",
        agency_id: editData?.agency?.id || editData?.agency_id || "",
        role_ids: editData?.roles?.map(r => r.id) || editData?.role_ids || [],
        permission_ids: editData?.permission_ids ?? [],
        slack_user_id: editData?.slack_user_id || "",
    });
    const [status, setStatus] = useState(editData?.status ?? "");
    const [directPermissionsOpen, setDirectPermissionsOpen] = useState(false);
    const axiosPrivate = useAxiosPrivate();
    const queryClient = useQueryClient();
    const currentUser = useZustandStore((s) => s?.currentUser);
    const { hasPermission } = usePermissions();
    const canListAgencies = hasPermission(PERMISSION_CODES.AGENCY_MANAGE);
    const agencyScoped = isAgencyScopedUser(currentUser);
    const scopedAgencyId = getAgencyIdFromUser(currentUser);

    React.useEffect(() => {
        if (open && agencyScoped && scopedAgencyId != null && !editData) {
            setFormData((prev) => ({ ...prev, agency_id: scopedAgencyId }));
        }
    }, [open, agencyScoped, scopedAgencyId, editData]);

    React.useEffect(() => {
        if (editData?.status !== undefined) setStatus(editData.status);
    }, [editData?.status]);

    React.useEffect(() => {
        if (!editData) return;
        setFormData((prev) => ({
            ...prev,
            username: editData.username || "",
            email: editData.email || "",
            first_name: editData.first_name || "",
            last_name: editData.last_name || "",
            agency_id: editData.agency?.id || editData.agency_id || "",
            role_ids: editData.role_ids ?? [],
            permission_ids: editData.permission_ids ?? [],
            slack_user_id: editData.slack_user_id || "",
        }));
    }, [editData]);

    useEffect(() => {
        if (!open) setDirectPermissionsOpen(false);
    }, [open]);

    const { data: rolesResult } = useQuery({
        queryKey: GET_ROLES_QUERY_KEY,
        queryFn: () => RoleAPIs.list({ axiosPrivate, limit: 100, offset: 0 }),
        enabled: open,
    });
    const roles = rolesResult?.data ?? [];

    const superAdminRoleId = roles?.find((r) => r.name === "super_admin")?.id;
    /** Scoped user.manage: only seekhoapp.com super_admin rows may change account (matches backend). */
    const seekhoSuperAdminAgencyEditable =
        !!editData &&
        superAdminRoleId != null &&
        (formData.role_ids || []).includes(superAdminRoleId) &&
        emailHasSeekhoAppDomain(formData.email);

    const showAgencyField = !agencyScoped || seekhoSuperAdminAgencyEditable;

    const { data: agenciesResult } = useQuery({
        queryKey: GET_AGENCIES_QUERY_KEY,
        queryFn: () => AgencyAPIs.list({ axiosPrivate, limit: 100, offset: 0 }),
        enabled: open && showAgencyField && canListAgencies,
    });
    const agencies = agenciesResult?.data ?? [];

    const { data: permissionsResult } = useQuery({
        queryKey: GET_PERMISSIONS_QUERY_KEY,
        queryFn: () => RoleAPIs.listPermissions({ axiosPrivate, limit: 100, offset: 0 }),
    });
    const permissions = permissionsResult?.data ?? [];

    const { mutate: submitUser, isPending } = useMutation({
        mutationFn: (payload) =>
            editData
                ? UserAPIs.update({ axiosPrivate, id: editData.id, payload })
                : UserAPIs.create({ axiosPrivate, payload }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: GET_USERS_QUERY_KEY });
            toast.success(`User ${editData ? 'updated' : 'created'} successfully!`);
            onClose();
        },
    });

    const { mutate: updateStatus, isPending: isStatusUpdating } = useMutation({
        mutationFn: (newStatus) =>
            UserAPIs.update({ axiosPrivate, id: editData.id, payload: { status: newStatus } }),
        onSuccess: (_, newStatus) => {
            setStatus(newStatus);
            queryClient.invalidateQueries({ queryKey: GET_USERS_QUERY_KEY });
            toast.success(`User ${newStatus === "approved" ? "approved" : "rejected"}.`);
        },
        onError: (error) => {
            toast.error(error?.response?.data?.error_message || error?.message);
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.email || !formData.first_name) return toast.error("Required fields are missing");

        // Spec dictates: first_name, last_name, email, status, agency_id, phone, source, is_deleted, role_ids
        let agencyIdPayload = formData.agency_id === "" || formData.agency_id == null ? null : Number(formData.agency_id);
        if (agencyScoped && (!editData || !seekhoSuperAdminAgencyEditable)) {
            agencyIdPayload = scopedAgencyId ?? null;
        }

        const payload = {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            agency_id: agencyIdPayload,
            role_ids: formData.role_ids,
            permission_ids: formData.permission_ids,
            slack_user_id: (formData.slack_user_id || "").trim(),
            username: (formData.username || "").trim(),
            phone: "",
            source: "web",
        };

        submitUser(payload);
    };

    return (
        <CustomModal
            title={editData ? "Edit User" : "Add New User"}
            open={open}
            onClose={onClose}
            handleConfirmation={handleSubmit}
            isLoading={isPending}
            handleConfirmationBtnText={editData ? "Update" : "Create"}
            className="md:w-[90%] md:max-w-lg lg:w-[42%] lg:max-w-xl"
        >
            <div className="space-y-4 py-4 max-h-[70vh] overflow-y-auto px-1 pb-6 min-w-0">
                {editData && (
                    <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-4 pb-4 border-b">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Account status</p>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${status === 'approved' ? 'bg-emerald-500/10 text-emerald-700' :
                                status === 'pending' ? 'bg-amber-500/10 text-amber-700' :
                                    status === 'suspended' ? 'bg-slate-200/80 text-slate-600' : 'bg-rose-500/10 text-rose-700'
                                }`}>
                                {status}
                            </span>
                            <div className="flex items-center gap-2">
                            {(status === "pending" || status === "rejected" || status === "suspended") && (
                                <button
                                    type="button"
                                    onClick={() => updateStatus("approved")}
                                    disabled={isStatusUpdating}
                                    className="inline-flex items-center px-3.5 py-1.5 rounded-lg text-sm font-medium text-emerald-700 bg-white border border-emerald-200/80 shadow-sm hover:bg-emerald-50 hover:border-emerald-300 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    Approve
                                </button>
                            )}
                            {(status === "pending" || status === "approved" || status === "suspended") && (
                                <button
                                    type="button"
                                    onClick={() => updateStatus("rejected")}
                                    disabled={isStatusUpdating}
                                    className="inline-flex items-center px-3.5 py-1.5 rounded-lg text-sm font-medium text-rose-700 bg-white border border-rose-200/80 shadow-sm hover:bg-rose-50 hover:border-rose-300 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    Reject
                                </button>
                            )}
                            </div>
                        </div>
                    </div>
                )}
                <div className="grid grid-cols-2 gap-4 pb-2">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                        <CustomInput
                            placeholder="First name"
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                        <CustomInput
                            placeholder="Last name"
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        />
                    </div>
                </div>
                <div className="min-w-0 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <CustomInput
                        placeholder="user@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full min-w-0"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                    <CustomInput
                        placeholder="Username"
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Slack user ID</label>
                    <CustomInput
                        placeholder="e.g. U0123ABCD"
                        value={formData.slack_user_id}
                        onChange={(e) => setFormData({ ...formData, slack_user_id: e.target.value })}
                    />
                    <p className="text-[11px] leading-snug text-gray-500 mt-1">
                        Lets the bot @mention this person in Slack updates. In Slack: profile → ⋮ → Copy member ID.
                    </p>
                </div>
                {showAgencyField && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Agency</label>
                        <ActionSelect
                            options={[{ label: "Internal", value: "" }, ...(agencies?.map((a, i) => ({ label: agencyDisplayName(a, i), value: a.id })) || [])]}
                            value={formData.agency_id === "" || formData.agency_id == null ? "" : formData.agency_id}
                            onChange={(val) => setFormData({ ...formData, agency_id: val === "" || val == null ? "" : Number(val) })}
                            placeholder="Select agency"
                        />
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Roles</label>
                    <div className="flex flex-wrap gap-2 pt-1 border-t border-gray-100">
                        {roles?.map(role => (
                            <label key={role.id} className="inline-flex items-center p-2 rounded-lg bg-gray-50 border border-gray-100 cursor-pointer hover:border-primaryPurple/30 transition-colors">
                                <input
                                    type="checkbox"
                                    className="rounded text-primaryPurple focus:ring-primaryPurple mr-2"
                                    checked={formData.role_ids.includes(role.id)}
                                    onChange={(e) => {
                                        const ids = e.target.checked
                                            ? [...formData.role_ids, role.id]
                                            : formData.role_ids.filter(id => id !== role.id);
                                        setFormData({ ...formData, role_ids: ids });
                                    }}
                                />
                                <span className="text-xs font-medium text-gray-700 capitalize">{role.name.replace('_', ' ')}</span>
                            </label>
                        ))}
                    </div>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50/80 overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setDirectPermissionsOpen((o) => !o)}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-gray-100/80 transition-colors"
                        aria-expanded={directPermissionsOpen}
                    >
                        <span>Direct permissions</span>
                        <ChevronDown className={cn("h-4 w-4 shrink-0 text-gray-500 transition-transform", directPermissionsOpen && "rotate-180")} />
                    </button>
                    {directPermissionsOpen && (
                        <div className="border-t border-gray-200 bg-white px-3 pb-3 pt-2">
                            <p className="text-[11px] leading-snug text-gray-500 mb-2">
                                For temporary access only. Use roles for permanent assignment.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {permissions?.map(perm => (
                                    <label key={perm.id} className="inline-flex items-center p-2 rounded-lg bg-gray-50 border border-gray-100 cursor-pointer hover:border-primaryPurple/30 transition-colors">
                                        <input
                                            type="checkbox"
                                            className="rounded text-primaryPurple focus:ring-primaryPurple mr-2"
                                            checked={formData.permission_ids.includes(perm.id)}
                                            onChange={(e) => {
                                                const ids = e.target.checked
                                                    ? [...formData.permission_ids, perm.id]
                                                    : formData.permission_ids.filter(id => id !== perm.id);
                                                setFormData({ ...formData, permission_ids: ids });
                                            }}
                                        />
                                        <span className="text-xs font-medium text-gray-700">{perm.code}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </CustomModal>
    );
};

export default AddUserModal;
